import { useMemo } from 'react';
import { useDoc } from '@/lib/firestore/hooks';
import { aiUsageDoc, settingsDoc } from '@/lib/firestore/refs';
import { defaultProvidersSettings } from '@shared/ai-providers';
import type { AiPricingSettings, AiProviderConfig, AiProviderId, AiProvidersSettings, AiUsageMonth } from '@shared/types';
import { AI_PROVIDER_IDS } from '@shared/types';

/** Provider configs merged over catalogue defaults, so every provider always has a full config object. */
export function useAiProviders(): { providers: Record<AiProviderId, AiProviderConfig>; enabled: AiProviderId[]; loading: boolean } {
  const { data, loading } = useDoc(settingsDoc<AiProvidersSettings>('aiProviders'));
  const providers = useMemo(() => {
    const base = defaultProvidersSettings();
    for (const id of AI_PROVIDER_IDS) base[id] = { ...base[id], ...(data?.providers?.[id] ?? {}) };
    return base;
  }, [data]);
  const enabled = useMemo(() => AI_PROVIDER_IDS.filter((id) => providers[id].enabled && providers[id].keyConfigured), [providers]);
  return { providers, enabled, loading };
}

export function useAiPricing() {
  const { data, loading } = useDoc(settingsDoc<AiPricingSettings>('aiPricing'));
  return { pricing: data?.models ?? {}, loading };
}

export const currentMonth = () => new Date().toISOString().slice(0, 7);

export function useAiUsage(month: string = currentMonth()) {
  const { data, loading } = useDoc<AiUsageMonth>(aiUsageDoc(month));
  return { usage: data?.providers ?? {}, loading };
}
